import React from "react";
import { formatNumbers } from "../../../helpers/functions.tsx";
import { VimplayTransaction } from "../../../models/VimplayTransaction.tsx";
import "../../../styles/Transaction.css";

interface CasinoTransactionDetailsProps {
    transactions: VimplayTransaction[];
    startIndex?: number;
}

export interface CasinoPlay {
    id: string;
    game: string;
    bet: number;
    win: number;
    net: number;
    refunded: boolean;
}

const txId = (tx: VimplayTransaction): string => tx.id || tx.transactionId || "";

const gameOf = (tx: VimplayTransaction): string =>
    tx.gameName?.trim() || (tx.gameId != null ? `Game ${tx.gameId}` : "Casino");

const netOf = (tx: VimplayTransaction): number => {
    if (tx.refunded) return 0;
    const amount = Number(tx.amountChange ?? 0);
    return Number.isFinite(amount) ? amount : 0;
};

/** Stake that actually left the wallet. A credit repeats the bet and is ignored. */
const stakeOf = (tx: VimplayTransaction): number => {
    if (tx.refunded || tx.kind === "credit") return 0;
    if (tx.kind === "debit") {
        const change = Number(tx.amountChange ?? 0);
        return change < 0 ? Math.abs(change) : 0;
    }
    const bet = Number(tx.betAmount ?? 0);
    return Number.isFinite(bet) && bet > 0 ? bet : 0;
};

const winOf = (tx: VimplayTransaction): number => {
    if (tx.refunded || tx.kind === "debit") return 0;
    const win = Number(tx.winAmount ?? 0);
    if (Number.isFinite(win) && win > 0) return win;
    const change = Number(tx.amountChange ?? 0);
    return change > 0 ? change : 0;
};

const playKey = (tx: VimplayTransaction): string => {
    if (tx.roundId) return `round:${tx.roundId}`;
    if (tx.kind === "credit" && tx.betTransactionId) return `tx:${tx.betTransactionId}`;
    return `tx:${txId(tx)}`;
};

const createdMillis = (tx: VimplayTransaction): number => {
    const created = tx.createdAt as { seconds?: number; toMillis?: () => number } | undefined;
    if (!created) return 0;
    if (typeof created.toMillis === "function") return created.toMillis();
    if (typeof created.seconds === "number") return created.seconds * 1000;
    return 0;
};

/**
 * One row per play. Vimplay sends the stake and the win as two
 * wallet calls; the player only made one bet.
 */
export function groupCasinoPlays(transactions: VimplayTransaction[]): CasinoPlay[] {
    const groups = new Map<string, VimplayTransaction[]>();
    const order: string[] = [];

    for (const tx of transactions) {
        const key = playKey(tx);
        const group = groups.get(key);
        if (group) {
            group.push(tx);
            continue;
        }
        groups.set(key, [tx]);
        order.push(key);
    }

    return order
        .map((key) => {
            const rows = groups.get(key) ?? [];
            const first = rows[0];
            const named = rows.find((tx) => tx.gameName?.trim());
            return {
                id: key,
                game: gameOf(named ?? first),
                bet: rows.reduce((sum, tx) => sum + stakeOf(tx), 0),
                win: rows.reduce((sum, tx) => sum + winOf(tx), 0),
                net: rows.reduce((sum, tx) => sum + netOf(tx), 0),
                refunded: rows.length > 0 && rows.every((tx) => tx.refunded === true),
                at: Math.min(...rows.map(createdMillis)),
            };
        })
        .sort((a, b) => a.at - b.at)
        .map((row) => ({
            id: row.id,
            game: row.game,
            bet: row.bet,
            win: row.win,
            net: row.net,
            refunded: row.refunded,
        }));
}

const money = (value: number): string => `$${formatNumbers(Math.abs(value))}`;

const playLabel = (play: CasinoPlay): string => {
    if (play.refunded) return `${play.game} · Refunded`;
    const parts: string[] = [];
    if (play.bet > 0) parts.push(`Bet ${money(play.bet)}`);
    if (play.win > 0) parts.push(`Won ${money(play.win)}`);
    if (parts.length === 0) return play.game;
    return `${play.game} · ${parts.join(" · ")}`;
};

export const CasinoTransactionDetails = ({
    transactions,
    startIndex = 0,
}: CasinoTransactionDetailsProps) => {
    const plays = groupCasinoPlays(transactions);
    if (plays.length === 0) return null;

    return (
        <>
            {plays.map((play, index) => {
                const value = play.refunded ? 0 : play.net;
                const rowBg = (startIndex + index) % 2 === 0 ? "#e6e6e6" : "#ffffff";

                return (
                    <tr key={play.id} className="transaction-row-t" style={{ backgroundColor: rowBg }}>
                        <td colSpan={8}>
                            <div className="bet-selection-container">
                                <div className="d-flex align-items-center">
                                    <span className="body-letters black-letters body-letters-hanging">
                                        <span className="casino-play-label inter">{playLabel(play)}</span>
                                    </span>
                                </div>
                                <div
                                    className={`selection-money ${
                                        value < 0 ? "negative black-letters"
                                            : value > 0 ? "positive black-letters"
                                                : "zero black-letters"
                                    }`}
                                >
                                    {value > 0 ? "+ $" : value < 0 ? "- $" : "$"}
                                    {formatNumbers(Math.abs(value))}
                                </div>
                            </div>
                        </td>
                    </tr>
                );
            })}
        </>
    );
};
